import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Divider,
  Grid,
  Header,
  Segment,
  Table,
  Input
} from 'semantic-ui-react';
import { servicesAPI } from '../../_services';
import { history } from '../../_helpers/';
import { copyToClipboard } from '../../utils';
import { ChargePoint } from '../../_components/map/Features/ChargePoint';
import { getLocalStorage, dbUTCDatetimeToLocal } from '../../utils';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';

const moment = extendMoment(Moment);

const NO_UID = '00000000-0000-0000-0000-000000000000';

export const ListHUBs = () => {
  const [hydraAons, setHydraAons] = useState([]);
  const [chargingStationStates, setChargingStationStates] = useState(null);
  const [hydraSessions, setHydraSessions] = useState([]);
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [localAuthorityFilter, setLocalAuthorityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortLabel, setSortLabel] = useState('ascending');
  const [sortedData, setSortedData] = useState([]);

  const sortTable = column => {
    setSortColumn(column);
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortLabel(sortLabel === 'ascending' ? 'descending' : 'ascending');

    localStorage.setItem('adminHydrasSrtColumn', column);
    localStorage.setItem(
      'adminHydrasSrtDir',
      sortDirection === 'asc' ? 'desc' : 'asc'
    );
  };

  useEffect(() => {
    async function getHydras() {
      try {
        const data = await servicesAPI.InventoryServices('getHydras');

        const hydraAons = data.map(hydraAon => {
          // Check for outdated timestamps
          const dtLastHeartbeat = moment.utc(
            hydraAon.last_heartbeat_time,
            'YYYY-MM-DDTHH:mm:ss,Z'
          );
          const dtNow = moment.utc().local();
          const elapsedMilliseconds = moment.duration(
            dtNow.diff(dtLastHeartbeat)
          )._milliseconds;

          // Flag comms lost
          hydraAon.no_heartbeat = elapsedMilliseconds >= 900 * 1000;

          return {
            project: hydraAon.project,
            description: hydraAon.description,
            tag_status: hydraAon.tag_status,
            local_authority: hydraAon.local_authority,
            muted: false,
            faulted: false,
            capped: false,
            num_charging: 0,
            total_arbitrated_limit: 0,
            hydra_aon: hydraAon
          };
        });

        setHydraAons(
          hydraAons
            ? _.orderBy(hydraAons, 'description').filter(
                ({ tag_status }) => tag_status !== 'INACTIVE'
              )
            : []
        );
      } catch (error) {}
    }

    async function getChargeStationStates() {
      try {
        let data = await servicesAPI.AdminServices(
          'getStatesByEntityType',
          'CHARGING_STATION'
        );

        const states = {};
        const keys = Object.keys(data);
        for (const key of keys) {
          try {
            const state = JSON.parse(data[key]);

            if (!state.hydra_aon) continue; // TDTODO Should never happen but it has
            if (!states[state.hydra_aon.reference])
              states[state.hydra_aon.reference] = {};
            states[state.hydra_aon.reference][state.phalanx.reference] = state;
          } catch (error) {}
        }

        // Calculate stats
        for (const hydra_ref of Object.keys(states)) {
          const phalanxes = states[hydra_ref];

          // Stats
          let numStopped = 0;
          let numFaulted = 0;
          let numCapped = 0;
          let numCharging = 0;
          let totalArbitratedLimit = 0;

          for (const phalanx_ref of Object.keys(phalanxes)) {
            const phalanx = phalanxes[phalanx_ref].phalanx;
            states[hydra_ref].hydra_aon = phalanxes[phalanx_ref].hydra_aon;

            if (phalanx.tag_status === 'INACTIVE') continue;

            if (phalanx.status === 'Stopped') numStopped++;
            if (phalanx.status === 'Faulted') numFaulted++;
            if (phalanx.status === 'Capped') numCapped++;
            if (
              phalanx.status === 'Occupied' &&
              phalanx.connected_lance_tag_id !== NO_UID &&
              phalanx.connected_lance_tag_id &&
              phalanx.transaction_id !== NO_UID
            ) {
              numCharging++;
              totalArbitratedLimit += phalanx.arbitrated_limit;
            }
          }

          states[hydra_ref].muted = numStopped;
          states[hydra_ref].faulted = numFaulted;
          states[hydra_ref].capped = numCapped;
          states[hydra_ref].num_charging = numCharging;
          states[hydra_ref].total_arbitrated_limit = totalArbitratedLimit;
        }

        setChargingStationStates(states);
      } catch (error) {}
    }

    getHydras();
    getChargeStationStates();

    // Use user's preferred filter/sort settings otherwise defaults
    setDescriptionFilter(getLocalStorage('adminHydrasFltrDesc', ''));
    setLocalAuthorityFilter(getLocalStorage('adminHydrasFltrLocalAuth', ''));
    setTypeFilter(getLocalStorage('adminHydrasFltrType', ''));

    sortTable(
      getLocalStorage('adminHydrasSrtColumn', 'total_arbitrated_limit')
    );
    const dir = getLocalStorage('adminHydrasSrtDir', 'desc');
    setSortDirection(dir);
    setSortLabel(dir === 'asc' ? 'descending' : 'ascending');
  }, []);

  // Merge Hydra states with most recent session data
  useEffect(() => {
    if (hydraAons.length === 0 || !chargingStationStates) return;

    let states = [];
    hydraAons.forEach(hydraAon => {
      const sessionState = chargingStationStates[hydraAon.hydra_aon.reference];

      let hubType = 'Hydra';
      if (hydraAon.project === 'AON') hubType = 'AON';
      else if (hydraAon.project === 'AXON') hubType = 'AXON';

      if (sessionState) {
        states.push({
          ...sessionState,
          local_authority: hydraAon.hydra_aon.local_authority,
          project: hydraAon.hydra_aon.project,
          description: hydraAon.hydra_aon.description,
          tag_status: hydraAon.hydra_aon.tag_status,
          hydra_aon: hydraAon.hydra_aon,
          hub_type: hubType,
          last_heartbeat_time: hydraAon.hydra_aon.last_heartbeat_time
        });
      } else {
        states.push({
          ...hydraAon,
          local_authority: hydraAon.hydra_aon.local_authority,
          hub_type: hubType,
          last_heartbeat_time: hydraAon.hydra_aon.last_heartbeat_time
        });
      }
    });

    // Iterate over Hydras
    setHydraSessions(states);
  }, [hydraAons, chargingStationStates]);

  useEffect(() => {
    if (hydraSessions.length === 0) return;

    const sorted =
      sortColumn && sortDirection
        ? _.orderBy(hydraSessions, sortColumn, sortDirection)
        : hydraSessions;

    setSortedData(
      sorted
        .filter(
          ({ description }) =>
            !descriptionFilter ||
            (description &&
              description
                .toLowerCase()
                .includes(descriptionFilter.toLowerCase()))
        )
        .filter(
          ({ local_authority }) =>
            !localAuthorityFilter ||
            (local_authority &&
              local_authority
                .toLowerCase()
                .includes(localAuthorityFilter.toLowerCase()))
        )
        .filter(
          ({ hub_type }) =>
            !typeFilter ||
            (hub_type &&
              hub_type.toLowerCase().includes(typeFilter.toLowerCase()))
        )
    );
  }, [
    hydraSessions,
    descriptionFilter,
    localAuthorityFilter,
    typeFilter,
    sortColumn,
    sortDirection
  ]);

  return (
    <Segment>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column>
            <Header as="h2">HUBs</Header>
          </Grid.Column>
          <Grid.Column
            floated="right"
            textAlign="right"
            mobile={6}
            tablet={4}
            computer={3}
          />
        </Grid.Row>
      </Grid>
      <Divider hidden />
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <Input
              type="text"
              placeholder="filter description"
              icon={{
                name: 'close',
                link: true,
                onClick: () => {
                  setDescriptionFilter('');
                  localStorage.setItem('adminHydrasFltrDesc', '');
                }
              }}
              value={descriptionFilter}
              onChange={(event, { value }) => {
                setDescriptionFilter(value);
                localStorage.setItem('adminHydrasFltrDesc', value);
              }}
            />
            {'   '}
            <Input
              type="text"
              placeholder="filter local authority"
              icon={{
                name: 'close',
                link: true,
                onClick: () => {
                  setLocalAuthorityFilter('');
                  localStorage.setItem('adminHydrasFltrLocalAuth', '');
                }
              }}
              value={localAuthorityFilter}
              onChange={(event, { value }) => {
                setLocalAuthorityFilter(value);
                localStorage.setItem('adminHydrasFltrLocalAuth', value);
              }}
            />
            {'   '}
            <Input
              type="text"
              placeholder="filter type"
              icon={{
                name: 'close',
                link: true,
                onClick: () => {
                  setTypeFilter('');
                  localStorage.setItem('adminHydrasFltrType', '');
                }
              }}
              value={typeFilter}
              onChange={(event, { value }) => {
                setTypeFilter(value);
                localStorage.setItem('adminHydrasFltrType', value);
              }}
            />
          </Grid.Column>
        </Grid.Row>
        <Table sortable celled fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={
                  sortColumn === 'total_arbitrated_limit' ? sortLabel : null
                }
                onClick={() => sortTable('total_arbitrated_limit')}
                style={{ textAlign: 'center', width: '60px' }}
              >
                State
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '60px' }}>
                Reference
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'description' ? sortLabel : null}
                onClick={() => sortTable('description')}
                style={{ width: '120px' }}
              >
                Description
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'local_authority' ? sortLabel : null}
                onClick={() => sortTable('local_authority')}
                style={{ width: '60px' }}
              >
                Local
                <br />
                Authority
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '40px' }}>
                Status
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'tag_status' ? sortLabel : null}
                onClick={() => sortTable('tag_status')}
                style={{ width: '50px' }}
              >
                Tag Status
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'hub_type' ? sortLabel : null}
                onClick={() => sortTable('hub_type')}
                style={{ width: '70px' }}
              >
                Type
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '70px' }}>
                Current Limit (A)
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={
                  sortColumn === 'total_arbitrated_limit' ? sortLabel : null
                }
                onClick={() => sortTable('total_arbitrated_limit')}
                style={{ width: '70px' }}
              >
                Total Allocated (A)
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'last_heartbeat_time' ? sortLabel : null}
                onClick={() => sortTable('last_heartbeat_time')}
                style={{ width: '70px' }}
              >
                Last Heartbeat
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedData.map((h, h_idx) => (
              <Table.Row key={h_idx}>
                <Table.Cell
                  style={{ textAlign: 'center' }}
                  onClick={() => {
                    copyToClipboard(h.hydra_aon.tag_id);
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  <ChargePoint
                    w={48}
                    h={48}
                    colour={
                      h.num_charging ? 'blue' : h.faulted ? 'red' : 'green'
                    }
                    cpId={String(h.num_charging).padStart(2, '0')}
                    l1={h.num_charging}
                    l2={h.num_charging}
                    l3={h.num_charging}
                    car={h.num_charging}
                    lance={h.num_charging}
                  />
                </Table.Cell>
                <Table.Cell
                  onClick={() => {
                    copyToClipboard(h.hydra_aon.reference);
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  {h.hydra_aon.reference}
                </Table.Cell>
                <Table.Cell
                  onClick={() => {
                    copyToClipboard(h.hydra_aon.description);
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  {h.hydra_aon.description}
                </Table.Cell>
                <Table.Cell
                  onClick={() => {
                    copyToClipboard(h.hydra_aon.local_authority);
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  {h.hydra_aon.local_authority}
                </Table.Cell>
                <Table.Cell
                  onClick={() => {
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  {h.hydra_aon.status}
                </Table.Cell>
                <Table.Cell
                  onClick={() => {
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  {['COMMISSIONING', 'MAINTENANCE'].includes(
                    h.hydra_aon.tag_status
                  ) ? (
                    <p style={{ color: 'red' }}>{h.hydra_aon.tag_status}</p>
                  ) : (
                    h.hydra_aon.tag_status
                  )}
                </Table.Cell>
                <Table.Cell
                  onClick={() => {
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  {h.hub_type}
                </Table.Cell>
                <Table.Cell
                  onClick={() => {
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  {h.hydra_aon.working_current_limit}
                </Table.Cell>
                <Table.Cell
                  onClick={() => {
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  {h.total_arbitrated_limit}
                </Table.Cell>
                <Table.Cell
                  onClick={() => {
                    history.push(`/admin/hubs/${h.hydra_aon.reference}`);
                  }}
                >
                  {h.hydra_aon.no_heartbeat ? (
                    <p style={{ color: 'red' }}>
                      {dbUTCDatetimeToLocal(h.hydra_aon.last_heartbeat_time)}
                    </p>
                  ) : (
                    dbUTCDatetimeToLocal(h.hydra_aon.last_heartbeat_time)
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Grid>
    </Segment>
  );
};
